import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const MAX_VISIBLE = 3;

const ChatNotificationPopup = ({ notifications, setNotifications }) => {
  const navigate = useNavigate();

  const dismiss = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, [setNotifications]);

  const openChat = useCallback((notification) => {
    dismiss(notification.id);
    navigate("/help-chat", {
      state: {
        chatId: notification.client_id,
        client_name: notification.client_name,
        mobile: notification.mobile,
      },
    });
  }, [dismiss, navigate]);

  // Auto dismiss after 4 seconds
  useEffect(() => {
    if (notifications.length === 0) return;
    const latest = notifications[notifications.length - 1];
    const timer = setTimeout(() => {
      dismiss(latest.id);
    }, 15000);
    return () => clearTimeout(timer);
  }, [notifications]);

  if (notifications.length === 0) return null;

  const visible = notifications.slice(-MAX_VISIBLE);

  return (
    <>
      <style>{`
        @keyframes dropDown {
          from { opacity: 0; transform: translateY(-80px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shrinkBar {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          top: 12,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "92%",
          maxWidth: 400,
        }}
      >
        {visible.map((n, index) => (
          <NotificationCard
            key={n.id}
            n={n}
            onDismiss={dismiss}
            onOpen={openChat}
            isLatest={index === visible.length - 1}
          />
        ))}
      </div>
    </>
  );
};

const NotificationCard = React.memo(({ n, onDismiss, onOpen, isLatest }) => (
  <div
    style={{
      backgroundColor: "#ffffff",
      borderRadius: 16,
      padding: "10px 14px",
      animation: "dropDown 0.3s ease",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      overflow: "hidden",
      position: "relative",
      borderLeft: "4px solid #8F0D3d",  // ✅ accent left border
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {/* Avatar */}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          backgroundColor: "#8F0D3d",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 700,
          fontSize: 15,
          flexShrink: 0,
        }}
      >
        {n.client_name?.charAt(0)?.toUpperCase() || "C"}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="f3"
          style={{
            margin: 0,
            fontSize: 13,
            color: "#1B1B1B",   // ✅ dark text
            fontWeight: 700,
          }}
        >
          {n.client_name}
        </p>
        <p
          className="f2"
          style={{
            margin: 0,
            fontSize: 12,
            color: "#888888",   // ✅ grey subtitle
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          New message
        </p>
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => onDismiss(n.id)}
          style={{
            padding: "5px 12px",
            borderRadius: 20,
            border: "1px solid #ddd",     // ✅ light border
            backgroundColor: "transparent",
            color: "#888",                 // ✅ grey text
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
          className="f3"
        >
          Close
        </button>
        <button
          onClick={() => onOpen(n)}
          style={{
            padding: "5px 12px",
            borderRadius: 20,
            border: "none",
            backgroundColor: "#8F0D3d",
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
          className="f3"
        >
          Open
        </button>
      </div>
    </div>

    {/* Progress bar */}
    {isLatest && (
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 2,
          backgroundColor: "#8F0D3d",
          animation: "shrinkBar 15s linear forwards",
        }}
      />
    )}
  </div>
));

export default ChatNotificationPopup;